import { z } from "zod";

export const registerSchema = z.object({
  email: z.string().trim().email().max(254),
  username: z
    .string()
    .trim()
    .min(3)
    .max(24)
    .regex(/^[a-zA-Z0-9_-]+$/),
  password: z.string().min(10).max(128),
});

export const loginSchema = z.object({
  email: z.string().trim().email().max(254),
  password: z.string().min(1).max(128),
});
const loadoutTechniqueId = z.string().min(1).max(80);
const loadoutTechniqueSlot = z.object({
  slotIndex: z.number().int().min(0).max(5),
  techniqueId: loadoutTechniqueId,
});

export const loadoutSchema = z.union([
  z.object({ slots: z.array(loadoutTechniqueSlot).max(6) }).strict(),
  // Retain support for clients that still send the original ordered list.
  z.object({ techniques: z.array(loadoutTechniqueId).max(6) }),
]);

export type RegisterRequest = z.infer<typeof registerSchema>;
export type LoginRequest = z.infer<typeof loginSchema>;
export type LoadoutRequest = z.infer<typeof loadoutSchema>;
export * from "./technique-archive.js";
export * from "./inventory-grant.js";
export * from "./region-encounter-timers.js";

export interface AuthenticatedIdentity {
  userId: string;
  sessionId: string;
}

export interface ApiErrorBody {
  error: { code: string; message: string };
}

export const ROOM_MESSAGES = {
  move: "player.move",
  focus: "player.focus",
  engage: "player.engage",
  leave: "player.leave",
  regionTransitionCommit: "region.transition.commit",
  regionEncounterTimersRequest: "region.encounter_timers.request",
} as const;

export const regionTransitionHandoffRequestSchema = z
  .object({ proof: z.string().min(1).max(4096) })
  .strict();

export type RegionTransitionHandoffRequest = z.infer<
  typeof regionTransitionHandoffRequestSchema
>;

export interface RegionTransitionHandoffResponse {
  committed: true;
}

export interface MoveIntent {
  x: number;
  y: number;
  unitIds?: string[];
}

export interface EngageIntent extends MoveIntent {
  targetId: string;
}

/** Largest squad supported by the Barracks progression and runtime protocol. */
export const MAX_SQUAD_SIZE = 10;
const unitIdsSchema = z
  .array(z.string().uuid())
  .min(1)
  .max(MAX_SQUAD_SIZE)
  .refine((ids) => new Set(ids).size === ids.length, {
    message: "Unit IDs must be unique",
  });

export const ENTITY_DIRECTION = {
  north: 0,
  east: 1,
  south: 2,
  west: 3,
} as const;

export * from "./projectile-direction.js";

export const moveIntentSchema = z
  .object({
    x: z.number().finite(),
    y: z.number().finite(),
    unitIds: unitIdsSchema.optional(),
  })
  .strict();

export const engageIntentSchema = z
  .object({
    targetId: z.string().min(1).max(160),
    x: z.number().finite(),
    y: z.number().finite(),
    unitIds: unitIdsSchema.optional(),
  })
  .strict();

export * from "./realtime-state.js";
